import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code2, Book, Rocket, Terminal, Boxes, TrendingUp } from 'lucide-react';
import './DeveloperResources.css';

const DeveloperResources = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const resources = [
    {
      icon: <Code2 size={32} />,
      title: 'Smart Contract SDKs',
      description: 'Deploy contracts in Solidity (EVM), Rust, or Move (WASM)',
      features: ['Multi-language support', 'One-click deployment', 'Cross-chain compatibility'],
    },
    {
      icon: <Book size={32} />,
      title: 'Developer Documentation',
      description: 'Comprehensive guides, tutorials, and API references',
      features: ['Architecture deep-dives', 'Integration guides', 'Code examples'],
    },
    {
      icon: <Rocket size={32} />,
      title: 'Grant Program',
      description: 'Up to $50K per project + technical support',
      features: ['Funding', 'Mentorship', 'Co-marketing'],
    },
    {
      icon: <Terminal size={32} />,
      title: 'CLI & Testing Tools',
      description: 'Command-line tools and local development environment',
      features: ['Local testnet', 'Debugging tools', 'Gas profiler'],
    },
    {
      icon: <Boxes size={32} />,
      title: 'Open Source',
      description: 'Full codebase, examples, and community contributions',
      features: ['Code repositories', 'Sample dApps', 'Templates'],
    },
    {
      icon: <Boxes size={32} />,
      title: 'Infrastructure APIs',
      description: 'REST & WebSocket APIs for real-time data access',
      features: ['Block data', 'Transaction status', 'Event subscriptions'],
    },
  ];

  return (
    <section id="developer" className="developer-alien" data-testid="developer-section" ref={ref}>
      <div className="section-container">
        <motion.div
          className="section-header-alien"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="section-tag">
            <div className="tag-line"></div>
            <span className="tag-text">FOR BUILDERS</span>
            <div className="tag-line"></div>
          </div>
          <h2 className="section-title-alien">
            <span className="title-normal">DEVELOPER</span>
            <span className="title-highlight">RESOURCES</span>
          </h2>
          <p className="section-subtitle-alien">
            Everything you need to build the next generation of decentralized applications
          </p>
        </motion.div>

        <div className="resources-grid-alien">
          {resources.map((resource, index) => (
            <motion.div
              key={index}
              className="resource-card-alien"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
              whileHover={{ y: -8, scale: 1.02 }}
              data-testid={`resource-card-${index}`}
            >
              <div className="resource-icon-alien">{resource.icon}</div>
              <h3 className="resource-title-alien">{resource.title}</h3>
              <p className="resource-description-alien">{resource.description}</p>
              <div className="resource-features">
                {resource.features.map((feature, idx) => (
                  <div key={idx} className="feature-tag">
                    <div className="feature-dot"></div>
                    {feature}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="dev-cta-section"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
        >
          <div className="dev-cta-content">
            <h3 className="dev-cta-title">Ready to Build on Nomoex Chain?</h3>
            <p className="dev-cta-description">
              Join thousands of developers building the future of decentralized finance. Get access to up to <span className="highlight-text">$50K in grants</span>, 
              comprehensive technical support, and early access to mainnet. Deploy once in <span className="highlight-text">Solidity, Rust, or Move</span> 
              and reach <span className="highlight-text">8+ Layer-1 ecosystems</span> instantly.
            </p>
            <div className="dev-cta-stats">
              <div className="dev-stat">
                <div className="dev-stat-value">$50K</div>
                <div className="dev-stat-label">Max Grant</div>
              </div>
              <div className="dev-stat-divider"></div>
              <div className="dev-stat">
                <div className="dev-stat-value">3</div>
                <div className="dev-stat-label">Languages</div>
              </div>
              <div className="dev-stat-divider"></div>
              <div className="dev-stat">
                <div className="dev-stat-value">8+</div>
                <div className="dev-stat-label">L1 Chains</div>
              </div>
            </div>
            <div className="dev-cta-buttons">
              <button className="cta-modern primary" data-testid="get-started-dev-btn">
                <span>Start Building Now</span>
              </button>
              <button className="cta-modern secondary" data-testid="join-discord-btn">
                <span>Join Developer Discord</span>
              </button>
              <button className="cta-modern secondary" data-testid="view-charts-btn">
                <TrendingUp size={20} />
                <span>View Price Charts</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DeveloperResources;